/**
 * Recomputes a course's derived metrics (student count, "% preferencias logradas", criteria
 * balance) from its current student list - used to update the UI live while dragging students
 * between courses, before the move is saved as a new version. Mirrors the same computation
 * web-api-colegios's getResultVersion does server-side (see its own doc comment), so what the
 * counselor sees while dragging matches what they'll get back after saving.
 */
export const computeCourseMetrics = (students) => {
  const studentCount = students.length

  const withAchieved = students.filter(student => (student.preferencesAchievedCount || 0) > 0)
  const preferencesAchievedPercent = studentCount
    ? Math.round((withAchieved.length / studentCount) * 100)
    : null

  const criteriaBalance = {}
  students.forEach(student => {
    const scores = student.criteriaScores
    if (!scores) return
    Object.entries(scores).forEach(([dimension, value]) => {
      if (value === null || value === undefined) return
      criteriaBalance[dimension] = criteriaBalance[dimension] || {}
      const key = String(value)
      criteriaBalance[dimension][key] = (criteriaBalance[dimension][key] || 0) + 1
    })
  })

  return { studentCount, preferencesAchievedPercent, criteriaBalance }
}

/**
 * Re-derives every student's "chosen classmates placed with them" (preferencesAchievedCount) for a
 * whole board after a move, then each course's metrics - a move changes it not only for the moved
 * student but for everyone who chose them or whom they chose. Mirrors getResultVersion's own
 * computation: counts the student's chosen classmates (chosenClassmateEnrollmentIds, from their
 * saved answer) that sit in the same course. Students without a saved answer
 * (chosenClassmateEnrollmentIds null - unmatched names, or who never answered) keep their count.
 * @param {Array<{students: Array<Object>}>} courses
 * @returns {Array<Object>} new courses (inputs are not mutated)
 */
export const recomputeClassmatesAchieved = (courses) => {
  const courseIndexByEnrollmentId = new Map()
  courses.forEach((course, index) => course.students.forEach(student => {
    if (student.enrollmentId) courseIndexByEnrollmentId.set(String(student.enrollmentId), index)
  }))

  return courses.map((course, index) => {
    const students = course.students.map(student => {
      if (!Array.isArray(student.chosenClassmateEnrollmentIds)) return student
      const preferencesAchievedCount = student.chosenClassmateEnrollmentIds
        .filter(id => courseIndexByEnrollmentId.get(String(id)) === index).length
      return preferencesAchievedCount === student.preferencesAchievedCount
        ? student
        : { ...student, preferencesAchievedCount }
    })
    return { ...course, students, ...computeCourseMetrics(students) }
  })
}
